import React, { useState } from "react";
import MathContent from "../../../../customComponents/MathContent";

import {
  Box,
  Typography,
  IconButton,
  CircularProgress,
  Alert,
  Button,
  Grid,
  Paper,
  Chip,
  TextField,
  LinearProgress,
  Divider,
  MenuItem,
  Select,
  InputLabel,
  FormControl,
  List,
  ListItem,
  ListItemAvatar,
  ListItemText,
  Tabs,
  Tab,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from "@mui/material";
import UserAvatar from "../../../../common/UserAvatar";
import {
  ArrowBack,
  EmojiEvents,
  Add,
  People,
  PlayArrow,
  Stop,
  ContentCopy,
  PersonAdd,
  Leaderboard,
} from "@mui/icons-material";
import { keyframes } from "@emotion/react";
import { useSelector } from "react-redux";
import { useNavigate, useParams, useLocation } from "react-router-dom";
import {
  useGetMyTournamentsQuery,
  useCreateTournamentMutation,
  useJoinTournamentMutation,
  useGetTournamentQuery,
  useGetTournamentParticipantsQuery,
  useGetTournamentRoundsQuery,
  useStartTournamentRoundMutation,
  useEndTournamentRoundMutation,
  useGetTournamentLeaderboardQuery,
  useInviteTournamentParticipantsMutation,
} from "../../../../../app/tournamentApiSlice";
import { useGetQuizResultQuery } from "../../../../../app/quizApiSlice";
import QuizPlayer from "../shared/QuizPlayer";

// ─── Championship keyframes ─────────────────────────────────────────────────────
const trophyFloat = keyframes`
  0%, 100% { transform: translateY(0) rotate(-3deg); }
  50%       { transform: translateY(-8px) rotate(3deg); }
`;
const shimmer = keyframes`
  0%   { background-position: -200% center; }
  100% { background-position: 200% center; }
`;
const podiumPop = keyframes`
  0%   { transform: scaleY(0); opacity: 0; }
  70%  { transform: scaleY(1.06); opacity: 1; }
  100% { transform: scaleY(1); opacity: 1; }
`;
const confettiDrop = keyframes`
  0%   { transform: translateY(-10px) rotate(0deg);   opacity: 1; }
  100% { transform: translateY(100px) rotate(540deg); opacity: 0; }
`;

// Championship background wrapper
const TournamentBg = ({ children, sx = {} }) => (
  <Box
    sx={{
      background: "linear-gradient(160deg, #1A0C00 0%, #2E1700 40%, #1A0C00 75%, #0D0600 100%)",
      borderRadius: 0,
      p: { xs: 2.5, sm: 3 },
      pt: { xs: 2, sm: 2 },
      minHeight: "100vh",
      position: "relative",
      overflow: "hidden",
      ...sx,
    }}
  >
    <Box
      sx={{
        position: "absolute", inset: 0,
        backgroundImage: "radial-gradient(rgba(245,158,11,0.07) 1px, transparent 1px)",
        backgroundSize: "26px 26px",
        pointerEvents: "none",
      }}
    />
    <Box sx={{ position: "relative", zIndex: 1 }}>{children}</Box>
  </Box>
);

// Championship HUD header
const TournamentHUD = ({ onBack, label, sublabel }) => (
  <Box
    sx={{
      background: "linear-gradient(90deg, #7C2D00 0%, #D97706 55%, #FBBF24 100%)",
      borderRadius: "16px",
      px: 2.5, py: 2, mb: 3,
      display: "flex", alignItems: "center", gap: 1.5,
    }}
  >
    {onBack && (
      <IconButton
        onClick={onBack}
        size="small"
        sx={{ color: "rgba(255,255,255,0.8)", bgcolor: "rgba(255,255,255,0.12)", borderRadius: "10px" }}
      >
        <ArrowBack fontSize="small" />
      </IconButton>
    )}
    <Box>
      <Typography sx={{ fontWeight: 900, fontSize: "0.62rem", color: "rgba(255,255,255,0.55)", letterSpacing: 3, textTransform: "uppercase" }}>{sublabel}</Typography>
      <Typography sx={{ fontWeight: 800, fontSize: "1.1rem", color: "#fff" }}>{label}</Typography>
    </Box>
    <Box sx={{ ml: "auto" }}>
      <EmojiEvents sx={{ fontSize: "1.8rem", color: "rgba(255,255,255,0.4)", animation: `${trophyFloat} 3s ease-in-out infinite` }} />
    </Box>
  </Box>
);

// ─── Helpers ──────────────────────────────────────────────────────────────────
const getInitials = (name = "") =>
  name.split(" ").slice(0, 2).map((w) => w[0]?.toUpperCase() || "").join("");

const getUserName = (user) => {
  const p = user?.auth_profile;
  if (p?.first_name || p?.last_name) return `${p.first_name || ""} ${p.last_name || ""}`.trim();
  return user?.username || user?.schoola_id || "—";
};

const getUserPhoto = (user) => user?.auth_profile?.photo || null;

const ROUND_TYPE_LABELS = { timed: "Timed", self_paced: "Self-Paced", round_robin: "Round Robin" };


// ─── Tournament Lobby ─────────────────────────────────────────────────────────
export const TournamentLobby = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { state: locationState } = useLocation();
  const authData = useSelector((state) => state.auth);
  const [tabValue, setTabValue] = useState(locationState?.defaultTab ?? 0);
  const [activeRoundQuiz, setActiveRoundQuiz] = useState(null);
  const [completedQuizId, setCompletedQuizId] = useState(null);
  const [showQuiz, setShowQuiz] = useState(false);
  const [showResult, setShowResult] = useState(false);
  const [inviteIds, setInviteIds] = useState("");
  const [roundForm, setRoundForm] = useState({ subject: "", topics: "", num_questions: 10 });
  const [copySuccess, setCopySuccess] = useState(false);
  const [error, setError] = useState("");

  const { data: tournamentData } = useGetTournamentQuery(id, { skip: !id, pollingInterval: 8000 });

  const tournament = tournamentData?.tournament || tournamentData;
  const isCompleted = tournament?.status === 'completed' ||
    (tournament?.current_round != null && tournament?.num_rounds != null &&
      Number(tournament.current_round) >= Number(tournament.num_rounds));

  const { data: participantsData, refetch: refetchParticipants } = useGetTournamentParticipantsQuery(id, { skip: !id, pollingInterval: isCompleted ? 0 : 8000 });
  const { data: roundsData, refetch: refetchRounds } = useGetTournamentRoundsQuery(id, { skip: !id, pollingInterval: isCompleted ? 0 : 8000 });
  const { data: leaderboardData } = useGetTournamentLeaderboardQuery(id, { skip: !id, pollingInterval: isCompleted ? 0 : 10000 });

  const { data: quizResultData } = useGetQuizResultQuery(
    { quiz_id: completedQuizId },
    { skip: !completedQuizId || !showResult }
  );

  const [startRound, { isLoading: isStartingRound }] = useStartTournamentRoundMutation();
  const [endRound, { isLoading: isEndingRound }] = useEndTournamentRoundMutation();
  const [inviteParticipants, { isLoading: isInviting }] = useInviteTournamentParticipantsMutation();

  const participants = participantsData?.participants || [];
  const rounds = roundsData?.rounds || [];
  const leaderboard = leaderboardData?.leaderboard || [];
  const isOrganizer = Number(tournament?.user_id) === Number(authData?.user?.id);
  const activeRound = rounds.find((r) => r.status === "active");

  const handleStartRound = async () => {
    setError("");
    try {
      const topics = roundForm.topics.split(",").map((t) => t.trim()).filter(Boolean);
      const result = await startRound({ id, subject: roundForm.subject || tournament?.subject, topics, num_questions: roundForm.num_questions }).unwrap();
      const quiz = result.round?.quiz || result.quiz;
      if (quiz) {
        setActiveRoundQuiz(quiz);
        setShowQuiz(true);
      }
      refetchRounds();
    } catch (err) {
      setError(err?.data?.message || "Failed to start round.");
    }
  };

  const handleEndRound = async (roundId) => {
    setError("");
    try {
      await endRound({ id, roundId }).unwrap();
      refetchRounds();
    } catch (err) {
      setError(err?.data?.message || "Failed to end round.");
    }
  };

  const handleInvite = async () => {
    const ids = inviteIds.split(",").map((s) => s.trim()).filter(Boolean);
    if (!ids.length) return;
    setError("");
    try {
      await inviteParticipants({ id, schoola_ids: ids }).unwrap();
      setInviteIds("");
      refetchParticipants();
    } catch (err) {
      setError(err?.data?.message || "Failed to invite participants.");
    }
  };

  const handleJoinRoundQuiz = () => {
    if (activeRound?.quiz) {
      setActiveRoundQuiz(activeRound.quiz);
      setShowQuiz(true);
    }
  };

  const handleCopyCode = () => {
    navigator.clipboard.writeText(tournament?.code || "")
      .then(() => { setCopySuccess(true); setTimeout(() => setCopySuccess(false), 2000); })
      .catch(() => {});
  };

  const handleQuizDone = (qid) => {
    setCompletedQuizId(qid);
    setShowQuiz(false);
    setShowResult(true);
  };

  if (showResult && completedQuizId) {
    const result = quizResultData?.result || quizResultData;
    const correct = result?.correct ?? result?.score ?? "—";
    const total = result?.total ?? result?.num_questions ?? "—";
    const points = result?.points ?? result?.xp_earned ?? null;
    const pct = (result?.correct != null && result?.total) ? Math.round((result.correct / result.total) * 100) : null;
    return (
      <TournamentBg>
        <TournamentHUD onBack={() => { setShowResult(false); setCompletedQuizId(null); }} label="Round Complete!" sublabel="Championship" />
        <Box sx={{ textAlign: "center", py: 2 }}>
          <EmojiEvents sx={{ fontSize: "4rem", color: "#FBBF24", mb: 1, animation: `${trophyFloat} 2s ease-in-out infinite` }} />
          <Typography sx={{ fontWeight: 900, fontSize: "1.5rem", color: "#fff", mb: 0.5 }}>Quiz Submitted!</Typography>
          <Typography sx={{ color: "rgba(255,255,255,0.55)", mb: 3, fontSize: "0.9rem" }}>Your answers have been recorded for this round.</Typography>
          {result && (
            <Box sx={{ display: "inline-flex", gap: 3, p: 2.5, bgcolor: "rgba(245,158,11,0.12)", border: "1.5px solid rgba(245,158,11,0.4)", borderRadius: "16px", mb: 3 }}>
              <Box>
                <Typography sx={{ fontWeight: 900, fontSize: "2rem", color: "#FBBF24", lineHeight: 1 }}>{correct}/{total}</Typography>
                <Typography sx={{ fontSize: "0.75rem", color: "rgba(255,255,255,0.5)", mt: 0.3 }}>Correct</Typography>
              </Box>
              {pct !== null && (
                <Box sx={{ borderLeft: "1px solid rgba(255,255,255,0.15)", pl: 3 }}>
                  <Typography sx={{ fontWeight: 900, fontSize: "2rem", color: pct >= 70 ? "#86efac" : pct >= 40 ? "#FBBF24" : "#fca5a5", lineHeight: 1 }}>{pct}%</Typography>
                  <Typography sx={{ fontSize: "0.75rem", color: "rgba(255,255,255,0.5)", mt: 0.3 }}>Score</Typography>
                </Box>
              )}
              {points !== null && (
                <Box sx={{ borderLeft: "1px solid rgba(255,255,255,0.15)", pl: 3 }}>
                  <Typography sx={{ fontWeight: 900, fontSize: "2rem", color: "#a78bfa", lineHeight: 1 }}>+{points}</Typography>
                  <Typography sx={{ fontSize: "0.75rem", color: "rgba(255,255,255,0.5)", mt: 0.3 }}>Points</Typography>
                </Box>
              )}
            </Box>
          )}
          <Box sx={{ display: "flex", gap: 1.5, justifyContent: "center", flexWrap: "wrap" }}>
            <Button
              variant="contained"
              onClick={() => { setShowResult(false); setCompletedQuizId(null); setTabValue(3); }}
              startIcon={<Leaderboard />}
              sx={{ background: "linear-gradient(90deg, #7C2D00, #D97706)", fontWeight: 800, borderRadius: "12px", boxShadow: "0 4px 12px rgba(217,119,6,0.4)" }}
            >
              View Leaderboard
            </Button>
            <Button
              variant="outlined"
              onClick={() => { setShowResult(false); setCompletedQuizId(null); }}
              sx={{ borderColor: "rgba(245,158,11,0.5)", color: "rgba(255,200,100,0.9)", fontWeight: 700, borderRadius: "12px" }}
            >
              Back to Tournament
            </Button>
          </Box>
        </Box>
      </TournamentBg>
    );
  }

  if (showQuiz && activeRoundQuiz) {
    return (
      <Box>
        {/* Championship quiz HUD */}
        <Box
          sx={{
            background: "linear-gradient(90deg, #7C2D00 0%, #D97706 55%, #FBBF24 100%)",
            borderRadius: "16px 16px 0 0",
            px: 2.5, py: 1.5, mb: 0,
            display: "flex", alignItems: "center", justifyContent: "space-between",
          }}
        >
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <Box sx={{ bgcolor: "rgba(255,255,255,0.2)", borderRadius: "8px", px: 1.2, py: 0.4 }}>
              <Typography sx={{ fontWeight: 900, fontSize: "0.7rem", color: "#fff", letterSpacing: 1 }}>ROUND {activeRound?.round_number || "?"}</Typography>
            </Box>
            <Typography sx={{ fontWeight: 700, fontSize: "0.88rem", color: "rgba(255,255,255,0.85)" }}>{activeRoundQuiz.subject}</Typography>
          </Box>
          <EmojiEvents sx={{ color: "rgba(255,255,255,0.5)", fontSize: "1.4rem" }} />
        </Box>
        <QuizPlayer
          quiz={activeRoundQuiz}
          onDone={handleQuizDone}
          tournamentCode={tournament?.code}
          accentColor="#D97706"
          mode="TOURNAMENT"
        />
      </Box>
    );
  }

  return (
    <TournamentBg>
      <TournamentHUD onBack={() => navigate("/lms/student/quiz/play/tournament")} label={tournament?.name || "Tournament"} sublabel="Championship" />

      {/* Chips row */}
      <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap", mb: 2 }}>
        <Chip size="small" label={ROUND_TYPE_LABELS[tournament?.round_type] || "—"} sx={{ bgcolor: "rgba(245,158,11,0.15)", color: "#FBBF24", border: "1px solid rgba(245,158,11,0.3)", fontWeight: 700 }} />
        <Chip size="small" label={`${tournament?.num_rounds || 1} round${tournament?.num_rounds > 1 ? "s" : ""}`} sx={{ bgcolor: "rgba(255,255,255,0.08)", color: "rgba(255,255,255,0.7)", fontWeight: 700 }} />
        {tournament?.code && (
          <Chip
            size="small" label={copySuccess ? "Copied!" : `Code: ${tournament.code}`}
            onClick={handleCopyCode}
            icon={<ContentCopy sx={{ fontSize: 14, color: "rgba(255,255,255,0.5) !important" }} />}
            sx={{
              cursor: "pointer",
              bgcolor: copySuccess ? "rgba(22,163,74,0.2)" : "rgba(255,255,255,0.08)",
              color: copySuccess ? "#86efac" : "rgba(255,255,255,0.7)",
              fontWeight: 700,
              border: copySuccess ? "1px solid rgba(22,163,74,0.4)" : "1px solid rgba(255,255,255,0.1)",
            }}
          />
        )}
      </Box>

      {error && <Alert severity="error" sx={{ mb: 2, bgcolor: "rgba(220,38,38,0.15)", color: "#fca5a5", border: "1px solid rgba(224,36,36,0.3)", "& .MuiAlert-icon": { color: "#fca5a5" } }}>{error}</Alert>}

      <Tabs
        value={tabValue} onChange={(_, v) => setTabValue(v)}
        sx={{
          mb: 2,
          "& .MuiTabs-indicator": { bgcolor: "#D97706" },
          "& .MuiTab-root": { color: "rgba(255,255,255,0.5)", fontWeight: 700, fontSize: "0.82rem" },
          "& .Mui-selected": { color: "#FBBF24 !important" },
        }}
      >
        <Tab label="Overview" />
        <Tab label={`Players (${participants.length})`} />
        <Tab label={`Rounds (${rounds.length})`} />
        <Tab label="Leaderboard" />
      </Tabs>

      {/* Overview tab */}
      {tabValue === 0 && (
        <Box>
          {/* Round progress bar */}
          {tournament?.num_rounds > 0 && (
            <Box sx={{ mb: 2.5, p: 2, bgcolor: "rgba(255,255,255,0.04)", borderRadius: "12px", border: "1px solid rgba(245,158,11,0.15)" }}>
              <Box sx={{ display: "flex", justifyContent: "space-between", mb: 0.8 }}>
                <Typography sx={{ fontWeight: 700, fontSize: "0.78rem", color: "rgba(255,255,255,0.55)" }}>Tournament Progress</Typography>
                <Typography sx={{ fontWeight: 800, fontSize: "0.78rem", color: "#FBBF24" }}>
                  Round {Math.min(tournament.current_round ?? 0, tournament.num_rounds)} of {tournament.num_rounds}
                </Typography>
              </Box>
              <LinearProgress
                variant="determinate"
                value={Math.min(((tournament.current_round ?? 0) / tournament.num_rounds) * 100, 100)}
                sx={{ height: 6, borderRadius: 3, bgcolor: "rgba(255,255,255,0.08)", "& .MuiLinearProgress-bar": { bgcolor: "#D97706", boxShadow: "0 0 6px rgba(217,119,6,0.5)" } }}
              />
            </Box>
          )}
          {(tournament?.status === 'completed' || (Number(tournament?.current_round) >= Number(tournament?.num_rounds) && !activeRound)) ? (
          <Box sx={{ p: 2.5, bgcolor: "rgba(22,163,74,0.12)", border: "2px solid rgba(22,163,74,0.4)", borderRadius: "16px", mb: 2, textAlign: "center", position: "relative", overflow: "hidden" }}>
              {/* Confetti dots */}
              {Array.from({ length: 10 }).map((_, i) => (
                <Box key={i} sx={{ position: "absolute", top: `${(i * 12) % 40}%`, left: `${(i * 10.3) % 100}%`, width: 7, height: 7, borderRadius: "50%", bgcolor: ["#FBBF24","#D97706","#86efac","#93c5fd","#fff"][i % 5], animation: `${confettiDrop} ${1.3 + (i % 4) * 0.25}s ${i * 0.08}s ease-in both` }} />)
              )}
              <EmojiEvents sx={{ fontSize: "3.5rem", color: "#FBBF24", mb: 1, animation: `${trophyFloat} 2s ease-in-out infinite`, position: "relative", zIndex: 1 }} />
              <Typography sx={{ fontWeight: 900, color: "#FBBF24", fontSize: "1.3rem", mb: 0.5, position: "relative", zIndex: 1 }}>Tournament Complete!</Typography>
              <Typography sx={{ color: "rgba(255,255,255,0.6)", fontSize: "0.88rem", mb: 2, position: "relative", zIndex: 1 }}>
                All {tournament?.num_rounds} round{tournament?.num_rounds > 1 ? "s" : ""} have been played. Check the final standings.
              </Typography>
              <Button
                variant="contained" startIcon={<Leaderboard />} onClick={() => setTabValue(3)}
                sx={{ background: "linear-gradient(90deg, #15803D, #22c55e)", fontWeight: 800, borderRadius: "12px", boxShadow: "0 4px 12px rgba(22,163,74,0.4)", position: "relative", zIndex: 1 }}
              >
                View Final Leaderboard
              </Button>
            </Box>
          ) : activeRound ? (
            <Box sx={{ p: 2.5, bgcolor: "rgba(245,158,11,0.12)", border: "1.5px solid rgba(245,158,11,0.4)", borderRadius: "16px", mb: 2 }}>
              <Typography sx={{ fontWeight: 700, color: "#FBBF24", mb: 0.5 }}>Round {activeRound.round_number} is Live!</Typography>
              <Typography sx={{ color: "rgba(255,255,255,0.6)", fontSize: "0.88rem", mb: 2 }}>
                {activeRound.quiz ? `Subject: ${activeRound.quiz.subject}` : "Quiz being prepared…"}
              </Typography>
              <Box sx={{ display: "flex", gap: 1 }}>
                {activeRound.quiz && (
                  <Button
                    variant="contained" size="small" onClick={handleJoinRoundQuiz}
                    startIcon={<PlayArrow />}
                    sx={{ background: "linear-gradient(90deg, #7C2D00, #D97706)", fontWeight: 800, borderRadius: "10px" }}
                  >
                    Play Now
                  </Button>
                )}
                {isOrganizer && (
                  <Button
                    variant="outlined" size="small" onClick={() => handleEndRound(activeRound.id)} disabled={isEndingRound}
                    startIcon={<Stop />}
                    sx={{ borderColor: "rgba(245,158,11,0.5)", color: "rgba(255,200,100,0.9)", fontWeight: 700, borderRadius: "10px" }}
                  >
                    End Round
                  </Button>
                )}
              </Box>
            </Box>
          ) : (
            <Box sx={{ p: 2, bgcolor: "rgba(255,255,255,0.05)", border: "1px solid rgba(255,255,255,0.12)", borderRadius: "14px", mb: 2 }}>
              <Typography sx={{ color: "rgba(255,255,255,0.6)", fontSize: "0.88rem" }}>
                {isOrganizer ? "No active round. Start a new round from the Rounds tab." : "Waiting for the organizer to start a round…"}
              </Typography>
            </Box>
          )}

          {isOrganizer && (
            <Box sx={{ p: 2, bgcolor: "rgba(255,255,255,0.05)", border: "1px solid rgba(255,255,255,0.1)", borderRadius: "14px", mb: 2 }}>
              <Typography sx={{ fontWeight: 700, mb: 1.5, display: "flex", alignItems: "center", gap: 0.5, color: "rgba(255,255,255,0.85)" }}>
                <PersonAdd sx={{ fontSize: 18, color: "#FBBF24" }} /> Invite by Schoola ID
              </Typography>
              <Box sx={{ display: "flex", gap: 1 }}>
                <TextField
                  fullWidth size="small" value={inviteIds}
                  onChange={(e) => setInviteIds(e.target.value)}
                  placeholder="schoola_id1, schoola_id2, …"
                  InputLabelProps={{ sx: { color: "rgba(255,255,255,0.5)" } }}
                  sx={{
                    "& .MuiOutlinedInput-root": {
                      color: "#fff", bgcolor: "rgba(255,255,255,0.06)", borderRadius: "10px",
                      "& fieldset": { borderColor: "rgba(245,158,11,0.3)" },
                      "&:hover fieldset": { borderColor: "rgba(245,158,11,0.6)" },
                      "&.Mui-focused fieldset": { borderColor: "#D97706" },
                    },
                    "& input::placeholder": { color: "rgba(255,255,255,0.3)" },
                  }}
                />
                <Button
                  variant="contained" onClick={handleInvite} disabled={isInviting || !inviteIds.trim()}
                  sx={{ background: "linear-gradient(90deg, #7C2D00, #D97706)", fontWeight: 800, borderRadius: "10px", whiteSpace: "nowrap" }}
                >
                  {isInviting ? <CircularProgress size={18} sx={{ color: "#fff" }} /> : "Invite"}
                </Button>
              </Box>
            </Box>
          )}
        </Box>
      )}

      {/* Participants tab */}
      {tabValue === 1 && (
        <List sx={{ p: 0 }}>
          {participants.map((p, i) => (
            <ListItem key={p.user_id || i} sx={{ px: 0, borderBottom: "1px solid rgba(255,255,255,0.07)" }}>
              <ListItemAvatar>
                <UserAvatar
                  src={getUserPhoto(p.user)}
                  name={getUserName(p.user)}
                  sx={{ border: "2px solid rgba(245,158,11,0.4)" }}
                />
              </ListItemAvatar>
              <ListItemText
                primary={<Typography sx={{ fontWeight: 700, color: "#fff", fontSize: "0.9rem" }}>{getUserName(p.user)}</Typography>}
                secondary={<Typography sx={{ color: "rgba(255,255,255,0.4)", fontSize: "0.78rem" }}>{p.user?.schoola_id || ""}</Typography>}
              />
              {Number(p.user_id) === Number(tournament?.user_id) && (
                <Chip size="small" label="Organizer" sx={{ bgcolor: "rgba(245,158,11,0.2)", color: "#FBBF24", border: "1px solid rgba(245,158,11,0.3)", fontWeight: 700 }} />
              )}
            </ListItem>
          ))}
          {!participants.length && <Typography sx={{ py: 2, textAlign: "center", color: "rgba(255,255,255,0.4)" }}>No participants yet.</Typography>}
        </List>
      )}

      {/* Rounds tab */}
      {tabValue === 2 && (
        <Box>
          {isOrganizer && Number(tournament?.current_round ?? 0) < Number(tournament?.num_rounds ?? 0) && !activeRound && (
            <Box sx={{ p: 2.5, bgcolor: "rgba(255,255,255,0.05)", border: "1.5px solid rgba(245,158,11,0.4)", borderRadius: "16px", mb: 2 }}>
              <Typography sx={{ fontWeight: 700, mb: 1.5, color: "rgba(255,255,255,0.85)" }}>Start Next Round</Typography>
              <Grid container spacing={1.5}>
                {[
                  { label: "Subject", field: "subject", xs: 12 },
                  { label: "Topics (comma-separated)", field: "topics", xs: 12 },
                ].map(({ label, field, xs }) => (
                  <Grid item xs={xs} key={field}>
                    <TextField
                      fullWidth size="small" label={label} value={roundForm[field]}
                      onChange={(e) => setRoundForm((f) => ({ ...f, [field]: e.target.value }))}
                      InputLabelProps={{ sx: { color: "rgba(255,255,255,0.5)" } }}
                      sx={{
                        "& .MuiOutlinedInput-root": {
                          color: "#fff", bgcolor: "rgba(255,255,255,0.06)", borderRadius: "10px",
                          "& fieldset": { borderColor: "rgba(245,158,11,0.3)" },
                          "&:hover fieldset": { borderColor: "rgba(245,158,11,0.6)" },
                          "&.Mui-focused fieldset": { borderColor: "#D97706" },
                        },
                      }}
                    />
                  </Grid>
                ))}
                <Grid item xs={12}>
                  <Typography sx={{ fontSize: "0.72rem", fontWeight: 700, color: "rgba(255,255,255,0.45)", letterSpacing: 1.5, textTransform: "uppercase", mb: 1 }}>
                    Questions
                  </Typography>
                  <Box sx={{ display: "flex", gap: 1 }}>
                    {[5, 10, 20].map((n) => (
                      <Box
                        key={n}
                        onClick={() => setRoundForm((f) => ({ ...f, num_questions: n }))}
                        sx={{
                          px: 2.5, py: 1, borderRadius: "10px", cursor: "pointer", fontWeight: 800, fontSize: "1rem",
                          color: roundForm.num_questions === n ? "#fff" : "rgba(255,255,255,0.5)",
                          background: roundForm.num_questions === n ? "linear-gradient(90deg, #7C2D00, #D97706)" : "rgba(255,255,255,0.06)",
                          border: roundForm.num_questions === n ? "1.5px solid rgba(217,119,6,0.8)" : "1.5px solid rgba(255,255,255,0.1)",
                          transition: "all 0.15s ease",
                          "&:hover": { background: roundForm.num_questions === n ? "linear-gradient(90deg, #7C2D00, #D97706)" : "rgba(255,255,255,0.1)" },
                        }}
                      >
                        {n}
                      </Box>
                    ))}
                  </Box>
                </Grid>
                <Grid item xs={6} sx={{ display: "flex", alignItems: "flex-end" }}>
                  <Button
                    fullWidth variant="contained" onClick={handleStartRound} disabled={isStartingRound}
                    startIcon={isStartingRound ? <CircularProgress size={16} sx={{ color: "#fff" }} /> : <PlayArrow />}
                    sx={{ background: "linear-gradient(90deg, #7C2D00, #D97706)", fontWeight: 800, borderRadius: "10px" }}
                  >
                    Start Round {(tournament?.current_round || 0) + 1}
                  </Button>
                </Grid>
              </Grid>
            </Box>
          )}

          {rounds.map((round) => (
            <Box
              key={round.id}
              sx={{
                p: 2, mb: 1.5, borderRadius: "14px",
                bgcolor: round.status === "active" ? "rgba(245,158,11,0.12)" : "rgba(255,255,255,0.05)",
                border: round.status === "active" ? "1.5px solid rgba(245,158,11,0.5)" : "1px solid rgba(255,255,255,0.1)",
              }}
            >
              <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                <Box>
                  <Typography sx={{ fontWeight: 700, color: "#fff" }}>Round {round.round_number}</Typography>
                  <Typography sx={{ fontSize: "0.82rem", color: "rgba(255,255,255,0.5)" }}>{round.quiz?.subject || "—"} · {round.quiz?.questions?.length || "?"} questions</Typography>
                </Box>
                <Chip
                  size="small" label={round.status}
                  sx={{
                    fontWeight: 700, fontSize: "0.7rem",
                    bgcolor: round.status === "active" ? "rgba(245,158,11,0.25)" : round.status === "completed" ? "rgba(22,163,74,0.2)" : "rgba(255,255,255,0.08)",
                    color: round.status === "active" ? "#FBBF24" : round.status === "completed" ? "#86efac" : "rgba(255,255,255,0.5)",
                  }}
                />
              </Box>
            </Box>
          ))}
          {!rounds.length && <Typography sx={{ py: 2, textAlign: "center", color: "rgba(255,255,255,0.4)" }}>No rounds yet.</Typography>}
        </Box>
      )}

      {/* Leaderboard tab */}
      {tabValue === 3 && (
        <Box>
          {[...leaderboard].sort((a, b) => (b.points || 0) - (a.points || 0)).slice(0, 10).map((entry, i) => (
            <Box
              key={entry.user_id || i}
              sx={{
                p: 2, mb: 1.5, borderRadius: "14px", display: "flex", alignItems: "center", gap: 2,
                bgcolor: i === 0 ? "rgba(245,158,11,0.15)" : i === 1 ? "rgba(200,200,200,0.08)" : i === 2 ? "rgba(185,115,50,0.1)" : "rgba(255,255,255,0.05)",
                border: i === 0 ? "1.5px solid rgba(245,158,11,0.4)" : i < 3 ? "1px solid rgba(255,255,255,0.12)" : "1px solid rgba(255,255,255,0.07)",
              }}
            >
              <Typography
                sx={{
                  fontWeight: 900, width: 30, textAlign: "center", fontSize: "1.2rem",
                  color: i === 0 ? "#FBBF24" : i === 1 ? "#D1D5DB" : i === 2 ? "#CD7F32" : "rgba(255,255,255,0.4)",
                }}
              >
                {i === 0 ? "🥇" : i === 1 ? "🥈" : i === 2 ? "🥉" : i + 1}
              </Typography>
              <UserAvatar
                src={getUserPhoto(entry.user)}
                name={getUserName(entry.user)}
                sx={{ border: i === 0 ? "2px solid #FBBF24" : "2px solid rgba(255,255,255,0.15)" }}
              />
              <Box sx={{ flex: 1 }}>
                <Typography sx={{ fontWeight: 700, color: "#fff", fontSize: "0.9rem" }}>{getUserName(entry.user)}</Typography>
                <Typography sx={{ fontSize: "0.75rem", color: "rgba(255,255,255,0.4)" }}>{entry.user?.schoola_id || ""}</Typography>
              </Box>
              <Typography sx={{ fontWeight: 900, color: i === 0 ? "#FBBF24" : "rgba(255,255,255,0.7)", fontSize: i === 0 ? "1.15rem" : "1rem" }}>
                {entry.points || 0} <Typography component="span" sx={{ fontSize: "0.7rem", color: "rgba(255,255,255,0.4)" }}>pts</Typography>
              </Typography>
            </Box>
          ))}
          {!leaderboard.length && <Typography sx={{ py: 2, textAlign: "center", color: "rgba(255,255,255,0.4)" }}>Leaderboard updates after rounds complete.</Typography>}
        </Box>
      )}
    </TournamentBg>
  );
};

// ─── Tournament Hub ────────────────────────────────────────────────────────────
const TournamentHub = () => {
  const navigate = useNavigate();
  const [showCreate, setShowCreate] = useState(false);
  const [joinCode, setJoinCode] = useState("");
  const [createForm, setCreateForm] = useState({ name: "", subject: "", round_type: "timed", num_rounds: 3, mode: "quiz" });
  const [error, setError] = useState("");

  const { data: myTournamentsData, isLoading } = useGetMyTournamentsQuery();
  const [createTournament, { isLoading: isCreating }] = useCreateTournamentMutation();
  const [joinTournament, { isLoading: isJoining }] = useJoinTournamentMutation();

  const tournaments = myTournamentsData?.tournaments || [];

  const handleCreate = async () => {
    setError("");
    if (!createForm.name.trim()) { setError("Tournament name is required."); return; }
    try {
      const result = await createTournament(createForm).unwrap();
      const t = result.tournament || result;
      setShowCreate(false);
      navigate(`/lms/student/quiz/play/tournament/${t.id}`);
    } catch (err) {
      setError(err?.data?.message || "Failed to create tournament.");
    }
  };

  const handleJoin = async () => {
    const code = joinCode.trim();
    if (!code) return;
    setError("");
    try {
      const result = await joinTournament({ code }).unwrap();
      const t = result.tournament || result;
      navigate(`/lms/student/quiz/play/tournament/${t.id}`);
    } catch (err) {
      setError(err?.data?.message || "Invalid code or tournament not found.");
    }
  };

  const formatDate = (d) => d ? new Date(d).toLocaleDateString("en-US", { month: "short", day: "numeric" }) : "—";

  return (
    <TournamentBg>
      <TournamentHUD onBack={() => navigate("/lms/student/quiz/play")} label="TOURNAMENTS" sublabel="Championship" />

      {error && <Alert severity="error" sx={{ mb: 2, bgcolor: "rgba(220,38,38,0.15)", color: "#fca5a5", border: "1px solid rgba(224,36,36,0.3)", "& .MuiAlert-icon": { color: "#fca5a5" } }}>{error}</Alert>}

      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid item xs={12} sm={6}>
          <Box
            onClick={() => setShowCreate(true)}
            sx={{
              p: 2.5, borderRadius: "16px",
              border: "2px dashed rgba(245,158,11,0.5)",
              cursor: "pointer", textAlign: "center",
              transition: "all 0.2s",
              bgcolor: "rgba(245,158,11,0.05)",
              "&:hover": { bgcolor: "rgba(245,158,11,0.12)", transform: "translateY(-3px)", border: "2px dashed rgba(245,158,11,0.8)" },
            }}
          >
            <Add sx={{ fontSize: "2.2rem", color: "#FBBF24", mb: 0.5 }} />
            <Typography sx={{ fontWeight: 800, color: "#fff" }}>Create Tournament</Typography>
            <Typography sx={{ fontSize: "0.82rem", color: "rgba(255,255,255,0.5)" }}>Start your own arena</Typography>
          </Box>
        </Grid>
        <Grid item xs={12} sm={6}>
          <Box sx={{ p: 2.5, borderRadius: "16px", bgcolor: "rgba(255,255,255,0.05)", border: "1px solid rgba(255,255,255,0.1)" }}>
            <Typography sx={{ fontWeight: 700, mb: 1.5, color: "rgba(255,255,255,0.85)" }}>Join by Code</Typography>
            <Box sx={{ display: "flex", gap: 1 }}>
              <TextField
                fullWidth size="small" placeholder="Enter tournament code"
                value={joinCode} onChange={(e) => setJoinCode(e.target.value.toUpperCase())}
                InputLabelProps={{ sx: { color: "rgba(255,255,255,0.5)" } }}
                sx={{
                  "& .MuiOutlinedInput-root": {
                    color: "#fff", bgcolor: "rgba(255,255,255,0.06)", borderRadius: "10px",
                    "& fieldset": { borderColor: "rgba(245,158,11,0.3)" },
                    "&:hover fieldset": { borderColor: "rgba(245,158,11,0.6)" },
                    "&.Mui-focused fieldset": { borderColor: "#D97706" },
                  },
                  "& input::placeholder": { color: "rgba(255,255,255,0.3)" },
                }}
              />
              <Button
                variant="contained" onClick={handleJoin} disabled={isJoining || !joinCode.trim()}
                sx={{ background: "linear-gradient(90deg, #7C2D00, #D97706)", fontWeight: 800, borderRadius: "10px", whiteSpace: "nowrap" }}
              >
                {isJoining ? <CircularProgress size={18} sx={{ color: "#fff" }} /> : "Join"}
              </Button>
            </Box>
          </Box>
        </Grid>
      </Grid>

      <Typography sx={{ fontWeight: 800, mb: 1.5, color: "rgba(255,255,255,0.85)" }}>My Tournaments</Typography>
      {isLoading && <LinearProgress sx={{ bgcolor: "rgba(255,255,255,0.08)", "& .MuiLinearProgress-bar": { bgcolor: "#D97706" } }} />}
      {!isLoading && !tournaments.length && (
        <Typography sx={{ py: 2, color: "rgba(255,255,255,0.4)", fontSize: "0.88rem" }}>You haven't joined any tournaments yet.</Typography>
      )}
      {tournaments.map((t) => (
        <Box
          key={t.id}
          onClick={() => navigate(`/lms/student/quiz/play/tournament/${t.id}`)}
          sx={{
            p: 2, mb: 1.5, borderRadius: "14px", cursor: "pointer",
            bgcolor: t.status === "active" ? "rgba(245,158,11,0.1)" : "rgba(255,255,255,0.05)",
            border: t.status === "active" ? "1.5px solid rgba(245,158,11,0.4)" : "1px solid rgba(255,255,255,0.08)",
            transition: "all 0.2s",
            "&:hover": { bgcolor: "rgba(245,158,11,0.12)", transform: "translateY(-2px)", border: "1.5px solid rgba(245,158,11,0.35)" },
          }}
        >
          <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start" }}>
            <Box>
              <Typography sx={{ fontWeight: 700, color: "#fff" }}>{t.name}</Typography>
              <Typography sx={{ fontSize: "0.8rem", color: "rgba(255,255,255,0.5)" }}>
                {t.subject || "—"} · {ROUND_TYPE_LABELS[t.round_type] || "—"} · {t.num_rounds} rounds
              </Typography>
            </Box>
            <Box sx={{ textAlign: "right" }}>
              <Chip
                size="small" label={t.status || "open"}
                sx={{
                  fontWeight: 700, fontSize: "0.7rem",
                  bgcolor: t.status === "active" ? "rgba(245,158,11,0.25)" : t.status === "completed" ? "rgba(22,163,74,0.2)" : "rgba(255,255,255,0.1)",
                  color: t.status === "active" ? "#FBBF24" : t.status === "completed" ? "#86efac" : "rgba(255,255,255,0.6)",
                }}
              />
              <Typography sx={{ fontSize: "0.72rem", color: "rgba(255,255,255,0.35)", mt: 0.5 }}>{formatDate(t.created_at)}</Typography>
            </Box>
          </Box>
        </Box>
      ))}

      {/* Create Dialog */}
      <Dialog open={showCreate} onClose={() => setShowCreate(false)} maxWidth="sm" fullWidth style={{ zIndex: 1500 }} PaperProps={{ sx: { borderRadius: "16px", bgcolor: "#1A0C00", color: "#fff", border: "1px solid rgba(245,158,11,0.3)" } }}>
        <DialogTitle sx={{ fontWeight: 700, color: "#fff", borderBottom: "1px solid rgba(255,255,255,0.08)" }}>Create Tournament</DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 0.5 }}>
            {[
              { label: "Tournament Name", field: "name", xs: 12 },
              { label: "Subject (optional)", field: "subject", xs: 12 },
            ].map(({ label, field, xs }) => (
              <Grid item xs={xs} key={field}>
                <TextField
                  fullWidth label={label} value={createForm[field]}
                  onChange={(e) => setCreateForm((f) => ({ ...f, [field]: e.target.value }))}
                  InputLabelProps={{ sx: { color: "rgba(255,255,255,0.5)" } }}
                  sx={{
                    "& .MuiOutlinedInput-root": {
                      color: "#fff", bgcolor: "rgba(255,255,255,0.06)", borderRadius: "10px",
                      "& fieldset": { borderColor: "rgba(245,158,11,0.3)" },
                      "&:hover fieldset": { borderColor: "rgba(245,158,11,0.6)" },
                      "&.Mui-focused fieldset": { borderColor: "#D97706" },
                    },
                  }}
                />
              </Grid>
            ))}
            <Grid item xs={6}>
              <FormControl fullWidth>
                <InputLabel sx={{ color: "rgba(255,255,255,0.5)" }}>Round Type</InputLabel>
                <Select
                  label="Round Type" value={createForm.round_type}
                  onChange={(e) => setCreateForm((f) => ({ ...f, round_type: e.target.value }))}
                  MenuProps={{ style: { zIndex: 1500 } }}
                  sx={{ color: "#fff", bgcolor: "rgba(255,255,255,0.06)", borderRadius: "10px", "& .MuiOutlinedInput-notchedOutline": { borderColor: "rgba(245,158,11,0.3)" }, "& .MuiSvgIcon-root": { color: "rgba(255,255,255,0.5)" } }}
                >
                  <MenuItem value="timed">Timed</MenuItem>
                  <MenuItem value="self_paced">Self-Paced</MenuItem>
                  <MenuItem value="round_robin">Round Robin</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={6}>
              <TextField
                fullWidth label="Number of Rounds" type="number" inputProps={{ min: 1, max: 10 }}
                value={createForm.num_rounds}
                onChange={(e) => setCreateForm((f) => ({ ...f, num_rounds: parseInt(e.target.value) || 1 }))}
                InputLabelProps={{ sx: { color: "rgba(255,255,255,0.5)" } }}
                sx={{ "& .MuiOutlinedInput-root": { color: "#fff", bgcolor: "rgba(255,255,255,0.06)", borderRadius: "10px", "& fieldset": { borderColor: "rgba(245,158,11,0.3)" }, "&.Mui-focused fieldset": { borderColor: "#D97706" } } }}
              />
            </Grid>
            <Grid item xs={12}>
              <FormControl fullWidth>
                <InputLabel sx={{ color: "rgba(255,255,255,0.5)" }}>Mode</InputLabel>
                <Select
                  label="Mode" value={createForm.mode}
                  onChange={(e) => setCreateForm((f) => ({ ...f, mode: e.target.value }))}
                  MenuProps={{ style: { zIndex: 1500 } }}
                  sx={{ color: "#fff", bgcolor: "rgba(255,255,255,0.06)", borderRadius: "10px", "& .MuiOutlinedInput-notchedOutline": { borderColor: "rgba(245,158,11,0.3)" }, "& .MuiSvgIcon-root": { color: "rgba(255,255,255,0.5)" } }}
                >
                  <MenuItem value="quiz">Quiz</MenuItem>
                  <MenuItem value="both">Quiz + Learn</MenuItem>
                </Select>
              </FormControl>
            </Grid>
          </Grid>
          {error && <Alert severity="error" sx={{ mt: 2, bgcolor: "rgba(220,38,38,0.15)", color: "#fca5a5", border: "1px solid rgba(224,36,36,0.3)", "& .MuiAlert-icon": { color: "#fca5a5" } }}>{error}</Alert>}
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2, borderTop: "1px solid rgba(255,255,255,0.08)" }}>
          <Button onClick={() => setShowCreate(false)} sx={{ color: "rgba(255,255,255,0.6)" }}>Cancel</Button>
          <Button
            variant="contained" onClick={handleCreate} disabled={isCreating}
            sx={{ background: "linear-gradient(90deg, #7C2D00, #D97706)", fontWeight: 800, borderRadius: "10px" }}
          >
            {isCreating ? <CircularProgress size={18} sx={{ color: "#fff" }} /> : "Create"}
          </Button>
        </DialogActions>
      </Dialog>
    </TournamentBg>
  );
};

export default TournamentHub;
